"use client"

import { useEffect, useState } from "react"

const RECIPIENT_TYPES = [
  "small_business",
  "veteran_owned_business",
  "minority_owned_business",
  "woman_owned_business"
]

// Format set-aside names for display
export function formatSetAsideName(name) {
  return name
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

async function fetchSetAsideData(type, naics, agency) {
  const isUnspecified = type === "unspecified"
  const filters = {
    time_period: [
      { start_date: "2024-10-01", end_date: "2025-09-30" },
      { start_date: "2023-10-01", end_date: "2024-09-30" },
      { start_date: "2022-10-01", end_date: "2023-09-30" },
      { start_date: "2021-10-01", end_date: "2022-09-30" },
    ],
    naics_codes: [naics],
  }

  if (agency) {
    filters.agencies = [
      {
        type: "awarding",
        tier: "toptier",
        name: agency,
      },
    ]
  }

  if (isUnspecified) {
    filters.excluded_recipient_type_names = RECIPIENT_TYPES
  } else {
    filters.recipient_type_names = [type]
  }
  try {
    const res = await fetch("https://api.usaspending.gov/api/v2/search/spending_by_category/awarding_agency", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        category: "awarding_agency",
        filters,
        subawards: false,
        spending_level: "awards",
      }),
    })

    if (!res.ok) throw new Error(`API error: ${res.status}`)
    const json = await res.json()

    return {
      set_aside: isUnspecified ? "unspecified" : type,
      formatted_name: isUnspecified ? "Unspecified" : formatSetAsideName(type),
      obligation: json?.results?.[0]?.amount || 0,
    }
  } catch (err) {
    console.error(`Error fetching data for ${type}:`, err)
    return {
      set_aside: isUnspecified ? "unspecified" : type,
      formatted_name: isUnspecified ? "Unspecified" : formatSetAsideName(type),
      obligation: 0,
    }
  }
}

export default function useSpendingBySetAside(naics, normalizedAgencyName) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!naics) {
      setData([])
      setLoading(false)
      return
    }

    const fetchAll = async () => {
      setLoading(true)
      setError(null)

      try {
        const allTypes = [...RECIPIENT_TYPES, "unspecified"]
        const responses = await Promise.all(
          allTypes.map((type) => fetchSetAsideData(type, naics, normalizedAgencyName))
        )
        setData(responses)
      } catch (err) {
        console.error("Error fetching set-aside data", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [naics, normalizedAgencyName])

  return { data, loading, error }
}
